import { localized } from "@msuite/ui/localization";
import {
	createColumnHelper,
	createMeta,
	createRowActions,
	createSubRowHandle,
} from "@msuite/ui/table";
import { Tag } from "@msuite/ui/tag";
import { formatNumber } from "@msuite/utils/numbers";
import { getWarehouseLocationNumberSegmentString } from "@msuite/utils/stock";
import { guardString } from "@msuite/utils/strings";
import type { WarehouseLocationListItem } from "@msuite/utils/types";
import { RowActions } from "./row-actions";

const columnHelper = createColumnHelper<WarehouseLocationListItem>();

/**
 * What one article has standing on the shelf. It is only ever rendered by
 * `articleWarehouseLocationColumns`, because the server leaves it null for a list that names no
 * article and a column reading 0 down every row says the shelves are empty rather than that
 * nothing was asked.
 */
const articleQuantityColumn = columnHelper.accessor("articleQuantity", {
	id: "articleQuantity",
	header: localized("stock.quantity_on_location"),
	cell: (cell) => (
		<Tag
			direction="row-reverse"
			color="slate"
			variant="plain"
		>
			{formatNumber(cell.getValue(), "integer", 0)}
		</Tag>
	),
	meta: createMeta({
		isNumeric: true,
	}),
});

export const columns = [
	createSubRowHandle<WarehouseLocationListItem>(),
	columnHelper.accessor("name", {
		id: "name",
		header: localized("general.name"),
		cell: (cell) => cell.getValue(),
		meta: createMeta({
			isTabular: true,
		}),
	}),
	columnHelper.accessor("warehouseZoneName", {
		id: "warehouseZoneName",
		header: localized("stock.warehouse_zone"),
		cell: (cell) => guardString(cell.getValue()),
		meta: createMeta({
			isTabular: true,
		}),
	}),
	columnHelper.accessor("shelf", {
		id: "shelf",
		header: localized("stock.warehouse_shelf"),
		cell: (cell) => getWarehouseLocationNumberSegmentString(cell.getValue()),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor("floor", {
		id: "floor",
		header: localized("stock.warehouse_floor"),
		cell: (cell) => getWarehouseLocationNumberSegmentString(cell.getValue()),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor("axis", {
		id: "axis",
		header: localized("stock.warehouse_axis"),
		cell: (cell) => getWarehouseLocationNumberSegmentString(cell.getValue()),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor("articleCount", {
		id: "articleCount",
		header: localized("stock.articles_on_location"),
		cell: (cell) => formatNumber(cell.getValue(), "integer", 0),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor("stockBalance", {
		id: "stockBalance",
		header: localized("stock.location_stock_balance"),
		cell: (cell) => (
			<Tag
				direction="row-reverse"
				color="slate"
				variant="plain"
			>
				{formatNumber(cell.getValue(), "integer", 0)}
			</Tag>
		),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	createRowActions(RowActions),
];

/**
 * The same set for a list narrowed to one article, with that article's own balance on each shelf
 * sitting beside the shelf's total. Both numbers matter together: a shelf holding 400 of which 40
 * are this article is a different answer to "where is it" than one holding 40 of nothing else.
 *
 * The expand handle goes with it. What sits under a shelf is the articles on it, and here the
 * article is the row above — leaving the column in draws a chevron that can never be clicked.
 */
export const articleWarehouseLocationColumns = columns.flatMap((column) => {
	if (column.id === "sub-row-handle") return [];
	return column.id === "stockBalance" ? [articleQuantityColumn, column] : [column];
});
